import { Form, ModalBody, ModalFooter, FormCheck, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import withModal from "../withModal";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";

import Btn from "../../ui/button/Btn";
import Input from "../../ui/form/Input";
import CustomSelect from "../../ui/form/CustomSelect";
import UpdateNoteModal from "../UpdateNoteModal";
import { PhoneIcon } from "../../icons/inputIcons";
import { getInitials } from "../../utility/functions";
import { followUpSchema } from "../../schema/validationSchema";
import { toLocalInput } from "../../utility/functions";

// Redux Actions
import { fetchLeadList } from "../../redux/slice/leadSlice";
import { fetchCustomerList } from "../../redux/slice/customerSlice";
import { fetchUsersList } from "../../redux/slice/usersSlice";
import { fetchAll } from "../../redux/slice/configurationSlice";
import { handleApiError } from "../../utility/handleApiError";
import { useToast } from "../../helpers/contexts/ToastContext";
import { createFollowup, updateFollowup } from "../../redux/slice/followupSlice";
import { useLocation } from "react-router-dom";

const normalizeOptions = (list = [], labelKey = "name", valueKey = "id") =>
  list.map((i) => ({
    label: String(i[labelKey] || ""),
    value: String(i[valueKey]),
    data: i,
  }));

const FollowUpModalInner = ({
  isEdit,
  onClose,
  data,
  onSave,
  from,
  contextData,
  isLoading,
  prefill, // optional { title, due_date, followup_type, description } for a new follow-up
}) => {
  const { showToast } = useToast();
  const formikRef = useRef();
  const dispatch = useDispatch();
  const debounceRef = useRef();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [prefillData] = useState(() => ({
    lead: location.state?.lead || null,
    customer: location.state?.customer || null,
  }));

  const [dropdownOptions, setDropdownOptions] = useState({
    followupTypes: [],
    users: [],
    leads: [],
    customers: [],
  });

  const [noteModal, setNoteModal] = useState({ isOpen: false, payload: null });
  const [isSubmittingWithNote, setIsSubmittingWithNote] = useState(false);

  const [loading, setLoading] = useState({
    followupTypes: false,
    users: false,
    leads: false,
    customers: false,
  });

  // Load initial options
  useEffect(() => {
    const loadInitial = async () => {
      const baseParams = { page: 1, limit: 10, is_active: true };
      try {
        setLoading((p) => ({
          ...p,
          users: true,
          followupTypes: true,
        }));
        const [users, types] = await Promise.all([
          dispatch(fetchUsersList(baseParams)).unwrap(),
          dispatch(fetchAll({ slug: "followup-types", params: baseParams })).unwrap(),
        ]);

        setDropdownOptions((prev) => ({
          ...prev,
          users: normalizeOptions(users?.data || []),
          followupTypes: normalizeOptions(types?.data || []),
        }));

        // Initial load for related based on 'from'
        if (from === "lead") {
          const res = await dispatch(fetchLeadList(baseParams)).unwrap();
          setDropdownOptions((p) => ({
            ...p,
            leads: normalizeOptions(res?.data || [], "lead_title"),
          }));
        } else if (from === "customer") {
          const res = await dispatch(fetchCustomerList(baseParams)).unwrap();
          setDropdownOptions((p) => ({
            ...p,
            customers: normalizeOptions(res?.data || [], "company_name"),
          }));
        }
      } catch (err) {
        console.error("Failed to load follow-up dropdowns", err);
      } finally {
        setLoading((p) => ({
          ...p,
          users: false,
          followupTypes: false,
        }));
      }
    };
    loadInitial();
  }, [dispatch, from]);

  const handleDropdownSearch = useCallback(
    (type, inputValue, meta) => {
      if (!meta || (meta.action !== "input-change" && meta.action !== "menu-open" && meta.action !== "set-value")) {
        return;
      }

      const searchText = String(inputValue || "");
      if (debounceRef.current) clearTimeout(debounceRef.current);

      setLoading((prev) => ({ ...prev, [type]: true }));

      // Immediately clear options on menu open to prevent stale "flash"
      if (meta && (meta.action === "menu-open" || meta.action === "input-change" || meta.action === "set-value")) {
        setDropdownOptions((prev) => ({ ...prev, [type]: [] }));
      }

      debounceRef.current = setTimeout(async () => {
        try {
          const params = {
            page: 1,
            limit: 10,
            is_active: true,
          };
          if (searchText) params.search = searchText;
          const actionMap = {
            users: fetchUsersList,
            leads: fetchLeadList,
            customers: fetchCustomerList,
            followupTypes: (p) =>
              dispatch(fetchAll({ slug: "followup-types", params: p })).unwrap(),
          };

          const action = actionMap[type];

          if (!action) return;

          let result;

          if (type === "followupTypes") {
            result = await action(params);
          } else {
            result = await dispatch(action(params)).unwrap();
          }

          setDropdownOptions((prev) => ({
            ...prev,
            [type]: normalizeOptions(
              result?.data || [],
              type === "leads" ? "lead_title" : type === "customers" ? "company_name" : "name"
            ),
          }));
        } catch (err) {
          console.error(`Dropdown search error [${type}]`, err);
        } finally {
          setLoading((prev) => ({ ...prev, [type]: false }));
        }
      }, 300);
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isEdit || !data) return;

    const resolveRelated = () => {
      let options = [];

      if (data.related_to === "customer") options = dropdownOptions.customers;
      if (data.related_to === "lead") options = dropdownOptions.leads;

      const selected = options.find((opt) => String(opt.value) === String(data.related_to_id));

      if (selected) {
        formikRef.current?.setFieldValue("related_to", selected, false);
      }
    };

    resolveRelated();
  }, [isEdit, data, dropdownOptions.customers, dropdownOptions.leads]);

  const initialValues = useMemo(() => {
    const baseInitial = {
      title: "",
      followup_type: null,
      followup_status: null,
      assigned_to: user ? { label: "Me", value: String(user.id) } : null,
      due_date: "",
      description: "",
      related_type: from || "lead",
      related_to: null,
      // "Plan next follow-up" fills these in
      ...(!isEdit && prefill
        ? {
            title: prefill.title || "",
            followup_type: prefill.followup_type || null,
            due_date: prefill.due_date ? toLocalInput(prefill.due_date) : "",
            description: prefill.description || "",
          }
        : {}),
    };

    if (isEdit && data) {
      const rType = data.related_to;
      let rTo = null;

      if (rType === "lead") {
        const l = data.related_record?.lead;
        if (l)
          rTo = {
            label: l.lead_title || l.title || l.label || "",
            value: data.related_to_id || l.id,
          };
      } else if (rType === "customer") {
        const c = data.related_record?.customer;

        if (c)
          rTo = {
            label: c.company_name || c.contact_person || c.name || c.label || "",
            value: data.related_to_id || c.id,
          };
      }

      const followupTypeId = data.followup_type?.id || data.followup_type_id;
      const followupStatusId = data.status?.id || (typeof data.followup_status === "object" ? data.followup_status?.id : null) || data.followup_status_id;
      const followupStatusName = data.status?.name || (typeof data.followup_status === "object" ? data.followup_status?.name : null) || (typeof data.followup_status === "string" ? data.followup_status : "");

      return {
        ...baseInitial,
        title: data.title || "",
        followup_type: followupTypeId
          ? { label: data.followup_type?.name, value: followupTypeId }
          : null,
        followup_status: followupStatusId
          ? {
              label: followupStatusName,
              value: followupStatusId,
            }
          : null,
        assigned_to: data.assigned_to
          ? {
              label: data.assigned_to?.name || data.assigned_to || "",
              value: data.assigned_to.id || data.assigned_to_id,
            }
          : null,
        due_date: toLocalInput(data.due_date),
        description: data.description || "",
        related_type: rType === "customer" ? "customer" : rType,
        related_to: rTo,
        reference_name: data.reference_name || "",
      };
    }

    // Build context for pre-fill
    const context = { ...prefillData };
    if (!isEdit && data) Object.assign(context, data);
    if (from && contextData) {
      context[from] = contextData;
    }

    const isObject = (val) => typeof val === "object" && val !== null;

    if (isObject(context.customer) && (context.customer.id || context.customer.value)) {
      const c = context.customer;
      return {
        ...baseInitial,
        related_type: "customer",
        related_to: {
          label:
            c.company_name || c.contact_person || c.people?.[0]?.name || c.name || c.label || "",
          value: c.id || c.value || "",
        },
      };
    }

    if (isObject(context.lead) && (context.lead.id || context.lead.value)) {
      return {
        ...baseInitial,
        related_type: "lead",
        related_to: {
          label: context.lead.lead_title || context.lead.title || context.lead.label || "",
          value: context.lead.id || context.lead.value || "",
        },
      };
    }

    return baseInitial;
  }, [isEdit, data, from, contextData, prefillData, user, prefill]);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setSubmitting(true);
    try {
      const payload = {
        title: values.title,
        followup_type_id: values.followup_type?.value,
        followup_status_id: values.followup_status?.value,
        assigned_to: values.assigned_to?.value,

        due_date: toLocalInput(values.due_date) || null,

        description: values.description,
        related_to: values.related_type,
        related_to_id: values.related_type === "other" ? null : values.related_to?.value,
        reference_name: values.reference_name,
      };

      if (isEdit) {
        setNoteModal({ isOpen: true, payload });
        return;
      }

      await executeSubmit(payload);
    } catch (err) {
      handleApiError(err, { setErrors, showToast });
    } finally {
      setSubmitting(false);
    }
  };

  const executeSubmit = async (payload) => {
    try {
      const result = isEdit
        ? await dispatch(updateFollowup({ id: data.id, payload })).unwrap()
        : await dispatch(createFollowup(payload)).unwrap();

      showToast(
        result?.message ||
          (isEdit ? "Follow-up Updated Successfully!" : "Follow-up Created Successfully!"),
        "success"
      );

      onSave?.();
      onClose();
    } catch (err) {
      handleApiError(err, { showToast });
      throw err;
    }
  };

  const handleNoteSubmit = async (note) => {
    if (!noteModal.payload) return;
    try {
      setIsSubmittingWithNote(true);
      const updatedPayload = { ...noteModal.payload, completion_note: note };
      await executeSubmit(updatedPayload);
      setNoteModal({ isOpen: false, payload: null });
    } catch (err) {
      // Handled in executeSubmit
    } finally {
      setIsSubmittingWithNote(false);
    }
  };

  const renderRelatedOptionLabel = (option, { context }, values) => {
    if (context !== "menu") return option.label;
    const item = option.data;
    if (!item) return option.label; // Safety guard for 'isCreatable' or manual options
    const type = values.related_type;

    let subLine = "";
    let phone = "";

    if (type === "lead") {
      subLine = item.lead_number || "";
      phone = item.person?.phone || item.phone || "";
    } else if (type === "customer") {
      subLine = item.email || item.company_name || item.name || "";
      phone = item.phone_number || item.phone || item.mobile || "";
    }

    return (
      <div className="d-flex align-items-center justify-content-between gap-3">
        <div className="d-flex align-items-center gap-3 min-w-0">
          <span
            className="rounded-circle border border-primary bg-primary text-white fw-semibold flex-center flex-shrink-0 fs-xs"
            style={{ width: "34px", height: "34px" }}
          >
            {getInitials(option.label)}
          </span>
          <div className="min-w-0">
            <p className="m-0 fw-medium text-primary text-truncate">{option.label}</p>
            <p className="m-0 fs-xs text-truncate opacity-75 text-secondary">{subLine}</p>
          </div>
        </div>
        <span className="d-flex align-items-center gap-1 flex-shrink-0 opacity-75 fs-xs text-secondary">
          {phone && <PhoneIcon size={12} />}
          <span>{phone || ""}</span>
        </span>
      </div>
    );
  };

  const getRelatedOptions = (type) => {
    switch (type) {
      case "lead":
        return dropdownOptions.leads;
      case "customer":
        return dropdownOptions.customers;
      default:
        return [];
    }
  };

  const isPrefilled = useMemo(() => {
    const context = { ...prefillData };
    if (from && contextData) {
      context[from] = contextData;
    }

    const isObject = (val) => typeof val === "object" && val !== null;

    return !!(
      (isObject(context.customer) && (context.customer.id || context.customer.value)) ||
      (isObject(context.lead) && (context.lead.id || context.lead.value))
    );
  }, [from, contextData, prefillData]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={followUpSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => (
        <Form
          onSubmit={handleSubmit}
          noValidate
          className="flex-grow-1 overflow-hidden flex-column"
        >
          <ModalBody className="p-12 overflow-y-auto my-0 custom-scroll">
            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "600px" }}
              >
                <Spinner animation="border" variant="primary" size="md"/>
              </div>
            ) : (
              <div className="grid columns-2 row-gap-8 column-gap-10">
                <div className="g-col-2 mb-4 flex-between gap-5">
                  {["lead", "customer", "other"].map((type) => (
                    <FormCheck
                      key={type}
                      type="radio"
                      label={type.charAt(0).toUpperCase() + type.slice(1)}
                      name="related_type"
                      id={`related_${type}`}
                      checked={values.related_type === type}
                      disabled={isPrefilled}
                      onChange={() => {
                        setFieldValue("related_type", type);
                        setFieldValue("related_to", null);

                        // Trigger API call for the selected type to lazy-load options
                        const mapping = {
                          lead: "leads",
                          customer: "customers",
                        };

                        if (mapping[type]) {
                          handleDropdownSearch(mapping[type], "", {
                            action: "menu-open",
                          });
                        }
                      }}
                    />
                  ))}
                </div>
                {["lead", "customer"].includes(values.related_type) ? (
                  <CustomSelect
                    label={{ name: "Related To" }}
                    selectProps={{
                      placeholder: `Select Related To ${values.related_type}`,
                    }}
                    className="g-col-2"
                    isDisabled={isPrefilled}
                    options={getRelatedOptions(values.related_type)}
                    value={values.related_to}
                    lookLikeInput={["customer", "lead"].includes(values.related_type)}
                    isLoading={
                      loading[
                        values.related_type === "lead"
                          ? "leads"
                          : values.related_type === "customer"
                            ? "customers"
                            : null
                      ]
                    }
                    formatOptionLabel={(option, meta) =>
                      renderRelatedOptionLabel(option, meta, values)
                    }
                    onChange={(val) => {
                      setFieldValue("related_to", val);
                    }}
                    onMenuOpen={() => {
                      const mapping = {
                        lead: "leads",
                        customer: "customers",
                      };

                      if (mapping[values.related_type]) {
                        handleDropdownSearch(mapping[values.related_type], "", {
                          action: "menu-open",
                        });
                      }
                    }}
                    onInputChange={(v, m) => {
                      const mapping = {
                        lead: "leads",
                        customer: "customers",
                      };
                      if (mapping[values.related_type]) {
                        handleDropdownSearch(mapping[values.related_type], v, m);
                      }
                    }}
                    filterOption={() => true}
                    required
                    validation={{
                      isInvalid: touched.related_to && !!errors.related_to,
                      message: errors.related_to,
                    }}
                  />
                ) : (
                  <Input
                    label={{ name: "Related To" }}
                    input={{
                      type: "text",
                      name: "reference_name",
                      value: values.reference_name,
                      onChange: handleChange,
                      onBlur: handleBlur,
                      placeholder: "Enter Related To",
                    }}
                    required={true}
                    className="g-col-2"
                    validation={{
                      isInvalid: touched.related_to && !!errors.related_to,
                      message: errors.related_to,
                    }}
                  />
                )}
                <Input
                  label={{ name: "Followup Title" }}
                  input={{
                    name: "title",
                    placeholder: "Enter Followup Title...",
                    value: values.title,
                    onChange: handleChange,
                    onBlur: handleBlur,
                  }}
                  className="g-col-2"
                  required
                  validation={{
                    isInvalid: touched.title && !!errors.title,
                    message: errors.title,
                  }}
                />

                <CustomSelect
                  label={{ name: "Followup Type" }}
                  selectProps={{ placeholder: "Select Followup Type" }}
                  options={dropdownOptions.followupTypes}
                  isLoading={loading.followupTypes}
                  value={values.followup_type}
                  onChange={(val) => setFieldValue("followup_type", val)}
                  onInputChange={(v, m) => handleDropdownSearch("followupTypes", v, m)}
                  onMenuOpen={() =>
                    handleDropdownSearch("followupTypes", "", {
                      action: "menu-open",
                    })
                  }
                  filterOption={() => true}
                  required
                  validation={{
                    isInvalid: touched.followup_type && !!errors.followup_type,
                    message: errors.followup_type,
                  }}
                />

                <CustomSelect
                  label={{ name: "Assign To" }}
                  selectProps={{ placeholder: "Select Assign To" }}
                  options={dropdownOptions.users}
                  isLoading={loading.users}
                  value={values.assigned_to}
                  onChange={(val) => setFieldValue("assigned_to", val)}
                  onMenuOpen={() =>
                    handleDropdownSearch("users", "", {
                      action: "menu-open",
                    })
                  }
                  onInputChange={(v, m) => handleDropdownSearch("users", v, m)}
                  filterOption={() => true}
                  validation={{
                    isInvalid: touched.assigned_to && !!errors.assigned_to,
                    message: errors.assigned_to,
                  }}
                />

                <Input
                  label={{ name: "Due Date" }}
                  input={{
                    type: "datetime-local",
                    name: "due_date",
                    value: values.due_date,
                    onChange: handleChange,
                    onBlur: handleBlur,
                    min: toLocalInput(new Date()),
                    placeholder: "DD/MM/YYYY HH:MM AM/PM",
                  }}
                  required={true}
                  validation={{
                    isInvalid: touched.due_date && !!errors.due_date,
                    message: errors.due_date,
                  }}
                />

                <Input
                  label={{ name: "Follow-up Description" }}
                  input={{
                    as: "textarea",
                    name: "description",
                    placeholder: "Enter Description...",
                    rows: 3,
                    value: values.description,
                    onChange: handleChange,
                    onBlur: handleBlur,
                  }}
                  className="g-col-2"
                  validation={{
                    isInvalid: touched.description && !!errors.description,
                    message: errors.description,
                  }}
                />
              </div>
            )}
          </ModalBody>
          <ModalFooter className="shadow border-top">
            <Btn
              variant="cancel"
              title="Cancel"
              className="modal-btn me-3"
              onClick={onClose}
              size="modal"
              disabled={isSubmitting}
            />
            <Btn
              type="submit"
              variant="primary"
              title={isEdit ? "Update" : "Create"}
              size="modal"
              disabled={isSubmitting}
            />
          </ModalFooter>

          <UpdateNoteModal
            isOpen={noteModal.isOpen}
            onClose={() => setNoteModal({ isOpen: false, payload: null })}
            onSubmit={handleNoteSubmit}
            isSubmitting={isSubmittingWithNote}
            title="Update Note"
            message="Please provide a note describing why you are updating this follow-up."
          />
        </Form>
      )}
    </Formik>
  );
};

const FollowUpModal = withModal(FollowUpModalInner);
export default FollowUpModal;
